import { Box, Tooltip } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React, { FC, useEffect, useState } from 'react';
import { useField, useForm, useFormState } from 'react-final-form';
import { IntlShape, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { OnChange } from 'react-final-form-listeners';
import moment from 'moment';
import {
  FieldCurrencyInput,
  IconCheckCircleFilled,
  IconInfo,
  InlineTextButton,
  TypographyWrapper,
} from '../../components';
import { getPrice } from '../../components/FieldCurrencyInput/FieldCurrencyInput';
import { useShopConfig } from '../../hooks/shopConfig';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { defaultTreetStyles } from '../../shopConfig/config';
import { CreditPayoutOption, isGiftCardOption } from '../../shopConfig/shops/default';
import {
  convertMoneyToNumber,
  convertNumberToMoney,
  formatDollarAmount,
  formatMoney,
} from '../../util/currency';
import {
  calculateMaximumPrice,
  calculateMinimumPrice,
  MAXIMUM_ALLOWABLE_PRICE,
} from '../../util/pricing';
import { types as sdkTypes } from '../../util/sdkLoader';
import * as validators from '../../util/validators';
import { parse } from '../../util/urlHelpers';
import Heading from './Heading';
import Subheading from './Subheading';
import {
  getRecommendedMinPriceForPriceDrop,
  getRecommendedPriceForCondition,
} from './EditListingPage.utils';
import { currencyConfiguration } from '../../currency-config';
import {
  getOriginalShopifyProductPrice,
  getShopifyProductVariantCompareAtAndPriceAverage,
} from '../../util/listings/listing';
import { Currency } from '../../types/apollo/generated/types.generated';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { useYotpo } from '../../hooks/useYotpo';
import {
  TypographyFormat,
  TypographyWeight,
  TypographyWrapperProps,
} from '../../components/TypographyWrapper/TypographyWrapper';
import { useCashPayoutPercentage } from '../../hooks/useCashPayoutPercentage';
import { FieldSwitch } from '../../components/FieldSwitch/FieldSwitch';
import { useActivePromos } from '../../hooks/useActivePromos';
import { Money as MoneyType } from '../../types/sharetribe/money';
import { ListingFlowFormField } from './ListingFlowTypes';
import { LISTING_STATE_PUBLISHED } from '../../util/types';

const { Money } = sdkTypes;

interface HelperTextProps extends Omit<TypographyWrapperProps, 'variant'> {
  className?: string;
  style?: CSSProperties;
}

const HelperText: FC<HelperTextProps> = (props) => {
  const { children, className, style, ...rest } = props;

  return (
    <TypographyWrapper
      variant="body2"
      typographyOverrides={{
        className,
        style: {
          color: defaultTreetStyles.gray60,
          fontSize: '12px',
          paddingTop: '4px',
          display: 'inline',
          ...style,
        },
      }}
      {...rest}
    >
      {children}
    </TypographyWrapper>
  );
};

interface GetPayoutHelperTextParams {
  intl: IntlShape;
  currencyConfig: any;
  creditPayoutPercentage: number; // credit payout percentage on listing
  cashPayoutPercentage: number;
  price: any;
  shopName: string;
  doesBrandUseLoyaltyPoints: boolean;
  getPointsFromPrice: (price: number) => string;
  configCreditPayoutPercentage: number; // credit payout percentage from config
  storeCreditName?: string;
}

const getPayoutHelperText = ({
  intl,
  currencyConfig,
  creditPayoutPercentage,
  cashPayoutPercentage,
  price,
  shopName,
  doesBrandUseLoyaltyPoints,
  getPointsFromPrice,
  configCreditPayoutPercentage,
  storeCreditName,
}: GetPayoutHelperTextParams) => {
  const cashPayoutAmount =
    price?.amount && formatDollarAmount(intl, price.amount * cashPayoutPercentage, currencyConfig);

  const creditPayoutAmount =
    price?.amount &&
    formatDollarAmount(intl, price.amount * creditPayoutPercentage, currencyConfig);

  const creditPayoutAmountBrandDefault =
    price?.amount &&
    configCreditPayoutPercentage &&
    formatDollarAmount(intl, price.amount * configCreditPayoutPercentage, currencyConfig);

  if (doesBrandUseLoyaltyPoints && price?.amount) {
    const potentialPointsEarned = getPointsFromPrice(price.amount);

    if (creditPayoutPercentage && cashPayoutPercentage) {
      return cashPayoutAmount && creditPayoutAmount ? (
        <>
          <HelperText>
            You will receive{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {potentialPointsEarned}
            </TypographyWrapper>
            points{' '}
          </HelperText>
          <HelperText weight={TypographyWeight.Bold}>
            (worth{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {creditPayoutAmount}
            </TypographyWrapper>
            )
          </HelperText>
          <HelperText>
            {' '}
            on{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {shopName}
            </TypographyWrapper>{' '}
            or{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {cashPayoutAmount}
            </TypographyWrapper>{' '}
            in cash when your item sells.
          </HelperText>
        </>
      ) : null;
    }
    if (creditPayoutPercentage) {
      return creditPayoutAmount ? (
        <>
          <HelperText>
            You will receive{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {potentialPointsEarned}
            </TypographyWrapper>
            points{' '}
          </HelperText>
          <HelperText weight={TypographyWeight.Bold}>
            (worth{' '}
            <TypographyWrapper
              variant="body2"
              weight={TypographyWeight.Bold}
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {creditPayoutAmount}
            </TypographyWrapper>
            )
          </HelperText>
          <HelperText>
            {' '}
            on{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{
                style: {
                  color: defaultTreetStyles.gray60,
                  fontSize: '12px',
                  paddingTop: '4px',
                  display: 'inline',
                },
              }}
              isDynamicValue
            >
              {shopName}
            </TypographyWrapper>{' '}
            when your item sells.
          </HelperText>
        </>
      ) : null;
    }
  }

  const listingHasCreditPromo = creditPayoutPercentage > configCreditPayoutPercentage;

  // Credit Boost Promo is currently unsupported for Yotpo.
  const creditPayoutTextMaybeWithPromo = listingHasCreditPromo ? (
    <>
      <HelperText style={{ textDecoration: 'line-through' }}>
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
              textDecoration: 'line-through',
            },
          }}
          isDynamicValue
        >
          {creditPayoutAmountBrandDefault}
        </TypographyWrapper>
      </HelperText>{' '}
      <HelperText weight={TypographyWeight.Bold}>
        <TypographyWrapper
          variant="body2"
          weight={TypographyWeight.Bold}
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
            },
          }}
          isDynamicValue
        >
          {creditPayoutAmount}
        </TypographyWrapper>
        *
      </HelperText>
    </>
  ) : (
    <TypographyWrapper
      variant="body2"
      typographyOverrides={{
        style: {
          color: defaultTreetStyles.gray60,
          fontSize: '12px',
          paddingTop: '4px',
          display: 'inline',
        },
      }}
      isDynamicValue
    >
      {creditPayoutAmount}
    </TypographyWrapper>
  );

  if (creditPayoutPercentage && cashPayoutPercentage) {
    return cashPayoutAmount && creditPayoutAmount ? (
      <>
        You’ll get {creditPayoutTextMaybeWithPromo} in{' '}
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
            },
          }}
          isDynamicValue
        >
          {shopName}
        </TypographyWrapper>{' '}
        {storeCreditName || 'credit'} or{' '}
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
            },
          }}
          isDynamicValue
        >
          {cashPayoutAmount}
        </TypographyWrapper>{' '}
        in cash when your item sells.
      </>
    ) : null;
  }

  if (creditPayoutPercentage) {
    return creditPayoutAmount ? (
      <>
        You’ll get {creditPayoutTextMaybeWithPromo} in{' '}
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
            },
          }}
          isDynamicValue
        >
          {shopName}
        </TypographyWrapper>{' '}
        credit when your item sells.
      </>
    ) : null;
  }

  if (cashPayoutPercentage) {
    return cashPayoutAmount ? (
      <>
        You’ll get{' '}
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{
            style: {
              color: defaultTreetStyles.gray60,
              fontSize: '12px',
              paddingTop: '4px',
              display: 'inline',
            },
          }}
          isDynamicValue
        >
          {cashPayoutAmount}
        </TypographyWrapper>{' '}
        in cash when your item sells.
      </>
    ) : null;
  }
  return null;
};

interface PriceDropTextProps {
  currentPrice: number;
  salePrice: number;
}

const PriceDropText: FC<PriceDropTextProps> = (props) => {
  const { currentPrice, salePrice } = props;

  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  return (
    <HelperText format={TypographyFormat.Underlined}>
      Recommended Price Drop:{' '}
      <HelperText weight={TypographyWeight.Bold} style={{ textDecoration: 'line-through' }}>
        {' '}
        <span id="recommended-price-drop-current-price">
          {intl.formatNumber(currentPrice / 100, currencyConfig)}
        </span>
      </HelperText>{' '}
      →
      <HelperText weight={TypographyWeight.Bold} style={{ color: defaultTreetStyles.red80 }}>
        <span id="recommended-price-drop-sale-price">
          {intl.formatNumber(salePrice, currencyConfig)}
        </span>
      </HelperText>
    </HelperText>
  );
};

const OriginalPrice: FC = () => {
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();
  const { currentListing } = useOwnListingVersions();
  const { shopifyProduct } = currentListing.attributes.publicData;
  const { values } = useFormState();
  const { shopifyProductVariant } = values;
  const [isEditingOriginalPrice, setIsEditingOriginalPrice] = useState(false);
  const originalPriceField = useField(ListingFlowFormField.OriginalPrice);
  const {
    listingFlowConfig: { originalPriceSources },
  } = useShopConfig();

  const originalPriceMessage = intl.formatMessage({ id: 'EditListingPricingForm.originalPrice' });
  const originalPricePlaceholderMessage = intl.formatMessage({
    id: 'EditListingPricingForm.originalPriceInputPlaceholder',
  });

  const shopifyOriginalPrice =
    shopifyProduct &&
    getOriginalShopifyProductPrice(
      currencyConfig.currency as Currency,
      shopifyProduct,
      shopifyProductVariant,
      originalPriceSources
    );
  const shopifyOriginalPriceCurrencyConfig =
    shopifyOriginalPrice?.currencyCode && currencyConfiguration(shopifyOriginalPrice.currencyCode);

  const tooltipContent = (color?: string) => {
    if (!shopifyOriginalPrice) {
      return null;
    }

    return (
      <Box marginX="8px">
        <Tooltip
          enterTouchDelay={0}
          title={
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{ color: 'inherit', display: 'inline' }}
            >
              We found the original price to be{' '}
              <span id="original-item-price">
                <TypographyWrapper
                  variant="body2"
                  typographyOverrides={{ display: 'inline', color: 'inherit' }}
                  isDynamicValue
                >
                  {intl.formatNumber(
                    shopifyOriginalPrice.amount,
                    shopifyOriginalPriceCurrencyConfig
                  )}
                </TypographyWrapper>
              </span>
              . If this is incorrect, please adjust it to be accurate.{' '}
            </TypographyWrapper>
          }
          placement="right"
        >
          <Box display="flex" alignItems="center">
            <IconInfo height="12" width="12" color={color} />
          </Box>
        </Tooltip>
      </Box>
    );
  };

  return (
    // Needs a minHeight so height doesn't change when toggling edit mode
    <Box display="flex" alignItems="center" minHeight="32px" marginY={2} width="300px">
      {isEditingOriginalPrice || !shopifyOriginalPrice ? (
        <>
          <TypographyWrapper variant="body1" typographyOverrides={{ style: { flex: '0 0 auto' } }}>
            {originalPriceMessage}:
          </TypographyWrapper>
          <FieldCurrencyInput
            id={ListingFlowFormField.OriginalPrice}
            name={ListingFlowFormField.OriginalPrice}
            placeholder={originalPricePlaceholderMessage}
            inputValue={values.originalPrice}
            autocomplete="off"
            input={{
              ...originalPriceField.input,
              onBlur: () => {
                setIsEditingOriginalPrice(false);
                return originalPriceField.input.onBlur;
              },
            }}
            boxContainerProps={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '8px',
              style: {
                columnGap: '8px',
                flexGrow: 1,
              },
            }}
            inputTypographyOverrides={{ style: { width: '100%' } }}
          />
          {tooltipContent()}
        </>
      ) : (
        <>
          <TypographyWrapper variant="body1">
            {originalPriceMessage}:{' '}
            <TypographyWrapper
              variant="body1"
              typographyOverrides={{ display: 'inline' }}
              isDynamicValue
            >
              {values.originalPrice ? formatMoney(intl, values.originalPrice) : ''}
            </TypographyWrapper>
          </TypographyWrapper>
          {tooltipContent(defaultTreetStyles.gray60)}
          <InlineTextButton
            onClick={() => setIsEditingOriginalPrice(true)}
            typographyVariant="body2"
            typographyFormat={TypographyFormat.Underlined}
            id="edit-original-price"
            title="Click to edit original retail price"
            typographyOverrides={{ style: { color: defaultTreetStyles.gray60 } }}
          >
            Edit
          </InlineTextButton>
        </>
      )}
    </Box>
  );
};

const SellingPrice: FC = () => {
  const intl = useIntl();
  const { shopName } = useShopConfig();
  const { currencyConfig } = useBrandCountryConfig();
  const { pricingRestrictions } = useListingFlowConfig();
  const { values } = useFormState({ subscription: { values: true } });
  const { price, originalPrice, itemType, tags } = values;
  const priceField = useField(ListingFlowFormField.Price);

  const priceMessage = intl.formatMessage({ id: 'EditListingPricingForm.price' });
  const pricePlaceholderMessage = intl.formatMessage({
    id: 'EditListingPricingForm.priceInputPlaceholder',
  });

  const originalPriceValue = originalPrice && convertMoneyToNumber(originalPrice);

  const calculatedMinimumPrice = calculateMinimumPrice(
    originalPriceValue,
    pricingRestrictions,
    tags,
    itemType
  );

  const minPrice = new Money(calculatedMinimumPrice, currencyConfig.currency);

  const calculatedMaximumPrice = calculateMaximumPrice(
    originalPriceValue,
    pricingRestrictions,
    tags
  );
  const maxPrice = new Money(calculatedMaximumPrice, currencyConfig.currency);

  const minPriceRequired = validators.moneySubUnitAmountAtLeast(
    intl.formatMessage(
      { id: 'EditListingPricingForm.priceTooLow' },
      { minPrice: formatMoney(intl, minPrice), brandName: shopName }
    ),
    calculatedMinimumPrice
  );
  const maxPriceRequired = validators.moneySubUnitAmountAtMost(
    'Exceeded maximum allowed price for this item.',
    calculatedMaximumPrice
  );
  const priceRequired = validators.required(
    intl.formatMessage({ id: 'EditListingPricingForm.priceRequired' })
  );
  const priceValidators =
    minPrice || maxPrice < MAXIMUM_ALLOWABLE_PRICE
      ? validators.composeValidators(priceRequired, minPriceRequired, maxPriceRequired)
      : priceRequired;

  return (
    <Box display="flex" minHeight="32px" marginY={1} width="300px">
      <TypographyWrapper variant="body1" typographyOverrides={{ style: { flex: '0 0 auto' } }}>
        {priceMessage}:
      </TypographyWrapper>
      <FieldCurrencyInput
        id={ListingFlowFormField.Price}
        name={ListingFlowFormField.Price}
        autocomplete="off"
        placeholder={pricePlaceholderMessage}
        validate={priceValidators}
        inputValue={price}
        input={priceField.input}
        inputTypographyOverrides={{ style: { width: '100%' } }}
        boxContainerProps={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: '8px',
          style: {
            columnGap: '8px',
            flexGrow: 1,
          },
        }}
      />
    </Box>
  );
};

const SellerPriceLearnMore: FC = () => {
  const {
    creditPayoutOption,
    mainSite,
    copy: { storeCreditPayoutLabel },
    treetShopName,
    creditPayoutPercentage,
    additionalPayoutSettings,
    storeCreditName,
  } = useShopConfig();
  const { creditCodeHelperText } = additionalPayoutSettings || {};

  if (!creditPayoutPercentage) {
    return <>Cash payouts are deposited directly into your bank account.</>;
  }

  const isPayoutGiftCard = isGiftCardOption(creditPayoutOption);

  const cashPayoutText =
    'Cash payouts are deposited directly into your bank account. You will be prompted to select ' +
    'cash or credit after the item has sold.';

  if (creditPayoutOption === CreditPayoutOption.PactCash) {
    return (
      <>
        Store credit is issued as Pact Cash applied to your Pact Customer account. This store credit
        is good for use on{' '}
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
        >
          {mainSite}
        </TypographyWrapper>{' '}
        only. It is{' '}
        <TypographyWrapper
          variant="body2"
          weight={TypographyWeight.Bold}
          typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
        >
          not
        </TypographyWrapper>{' '}
        redeemable on {treetShopName}. {creditCodeHelperText} {cashPayoutText}
      </>
    );
  }

  return (
    <>
      {storeCreditPayoutLabel ||
        (isPayoutGiftCard ? (
          <>
            {storeCreditName
              ? storeCreditName.replace(/\b\w/g, (name: string) => name.toUpperCase())
              : 'Store credit'}{' '}
            is issued via a for use on{' '}
            <TypographyWrapper
              variant="body2"
              weight={TypographyWeight.Bold}
              typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
            >
              {mainSite}
            </TypographyWrapper>{' '}
            only. It is{' '}
            <TypographyWrapper
              variant="body1"
              weight={TypographyWeight.Bold}
              typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
            >
              not
            </TypographyWrapper>{' '}
            redeemable on {treetShopName}. {creditCodeHelperText} {cashPayoutText}
          </>
        ) : (
          <>
            Store credit is issued via a discount code for use on any one purchase. This store
            credit is good for use on{' '}
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
            >
              {mainSite}
            </TypographyWrapper>{' '}
            only. It is{' '}
            <TypographyWrapper
              variant="body2"
              weight={TypographyWeight.Bold}
              typographyOverrides={{ display: 'inline', style: { fontSize: '12px' } }}
            >
              not
            </TypographyWrapper>{' '}
            redeemable on {treetShopName}. {creditCodeHelperText} {cashPayoutText}
          </>
        ))}
    </>
  );
};

const SellerPriceInfo: FC = () => {
  const {
    shopName,
    creditPayoutPercentage: configCreditPayoutPercentage,
    resaleValueConfig,
    storeCreditName,
  } = useShopConfig();

  const { conditionToRecommendedDiscount, pricingRestrictions } = useListingFlowConfig();
  const { currencyConfig } = useBrandCountryConfig();
  const intl = useIntl();
  const cashPayoutPercentage = useCashPayoutPercentage();

  const location = useLocation();
  const queryParams = parse(location.search);
  const { recommendedPriceDrop } = queryParams;

  const form = useForm();
  const { values, initialValues } = useFormState();
  const { category, condition, price, originalPrice, tags, shopifyProductVariant } = values;
  const { price: initialPrice } = initialValues;
  const { doesBrandUseLoyaltyPoints, getPointsFromPrice } = useYotpo();
  const { activePayoutCreditBoostPromo } = useActivePromos();
  const { currentListing } = useOwnListingVersions();
  const { creditPayoutPercentage } = currentListing.attributes?.publicData?.payoutInfo || {};
  const { promoEndDate } = activePayoutCreditBoostPromo || {};

  const [isLearnMoreExpanded, setIsLearnMoreExpanded] = useState<boolean>(false);

  const shopifyVariantCompareAtPriceAndPriceAverage =
    getShopifyProductVariantCompareAtAndPriceAverage(shopifyProductVariant);

  const priceToUse =
    resaleValueConfig && shopifyVariantCompareAtPriceAndPriceAverage
      ? convertNumberToMoney(shopifyVariantCompareAtPriceAndPriceAverage, currencyConfig.currency)
      : originalPrice;

  let calculatedRecommendedPrice = getRecommendedPriceForCondition({
    price: priceToUse,
    condition,
    conditionToRecommendedDiscount,
    pricingRestrictions,
    tags,
    category,
    resaleValueConfig,
  });

  if (recommendedPriceDrop) {
    calculatedRecommendedPrice = (initialPrice?.amount * (100 - recommendedPriceDrop)) / 10000;
  }

  const [recommendedPrice, setRecommendedPrice] = useState(calculatedRecommendedPrice);
  const initialPriceAmount = currentListing?.attributes?.price?.amount || 0;

  const formattedRecommendedPrice = intl.formatNumber(recommendedPrice, currencyConfig);

  useEffect(() => {
    if (calculatedRecommendedPrice && (Number.isNaN(recommendedPrice) || !recommendedPriceDrop)) {
      setRecommendedPrice(calculatedRecommendedPrice);
      form.change(
        ListingFlowFormField.RecommendedPrice,
        getPrice(recommendedPrice, currencyConfig)
      );
    }
  }, [calculatedRecommendedPrice]);

  const payoutHelperText = getPayoutHelperText({
    intl,
    currencyConfig,
    creditPayoutPercentage,
    cashPayoutPercentage,
    price,
    shopName,
    doesBrandUseLoyaltyPoints,
    getPointsFromPrice,
    configCreditPayoutPercentage,
    storeCreditName,
  });

  const hasCreditBoostPromo = creditPayoutPercentage > configCreditPayoutPercentage;
  const creditBoostPromoHelperText = hasCreditBoostPromo ? (
    <Box>
      <HelperText weight={TypographyWeight.Bold}>*BRAND CREDIT BOOST</HelperText>{' '}
      {promoEndDate && (
        <HelperText component="span">
          when you list by {moment(promoEndDate).format('MMM D')}
        </HelperText>
      )}
    </Box>
  ) : null;

  const sellingPriceMatchesRecommendedPrice = price?.amount === Math.round(recommendedPrice * 100);

  return (
    <>
      {!!Number(recommendedPrice) && (
        <Box display="flex" justifyContent="left" alignItems="center" marginY={1}>
          {sellingPriceMatchesRecommendedPrice ? (
            <>
              <IconCheckCircleFilled height="17" width="17" color={defaultTreetStyles.green60} />
              <HelperText
                style={{
                  color: defaultTreetStyles.green60,
                  paddingTop: 'unset',
                  marginLeft: '8px',
                }}
                weight={TypographyWeight.Bold}
              >
                Recommended Price
              </HelperText>
            </>
          ) : (
            <InlineTextButton
              onClick={() => {
                form.change(ListingFlowFormField.Price, getPrice(recommendedPrice, currencyConfig));
              }}
            >
              {recommendedPriceDrop ? (
                <PriceDropText currentPrice={initialPriceAmount} salePrice={recommendedPrice} />
              ) : (
                <HelperText
                  component="span"
                  style={{ paddingTop: 'unset' }}
                  format={TypographyFormat.Underlined}
                >
                  Use Recommended Price:{' '}
                  <span id="treet-recommended-price">
                    <TypographyWrapper
                      component="span"
                      variant="body2"
                      typographyOverrides={{ display: 'inline' }}
                      weight={TypographyWeight.Medium}
                      isDynamicValue
                    >
                      {formattedRecommendedPrice}
                    </TypographyWrapper>
                  </span>
                </HelperText>
              )}
            </InlineTextButton>
          )}
        </Box>
      )}
      <Box marginY={2}>
        <HelperText style={{ paddingTop: 'unset' }}>
          <>
            {payoutHelperText || ''}
            {!!payoutHelperText && isLearnMoreExpanded && (
              <>
                {' '}
                <SellerPriceLearnMore />
              </>
            )}
            {!!payoutHelperText && !isLearnMoreExpanded && (
              <InlineTextButton onClick={() => setIsLearnMoreExpanded(true)}>
                <HelperText
                  component="span"
                  style={{ paddingLeft: '4px', paddingTop: 'unset' }}
                  weight={TypographyWeight.Medium}
                  format={TypographyFormat.Underlined}
                >
                  Learn more
                </HelperText>
              </InlineTextButton>
            )}
          </>
        </HelperText>
        {!!payoutHelperText && creditBoostPromoHelperText}
      </Box>
    </>
  );
};

const AutomaticPriceDrop: FC = () => {
  const intl = useIntl();
  const form = useForm();
  const { shopName } = useShopConfig();
  const { currencyConfig } = useBrandCountryConfig();
  const { pricingRestrictions } = useListingFlowConfig();
  const { values } = useFormState();
  const { currentListing, savedListing } = useOwnListingVersions();
  const {
    price,
    originalPrice,
    tags,
    automaticPriceDropEnabled,
    automaticPriceDropPercentage,
    automaticPriceDropAnchorPrice,
    itemType,
  } = values;
  const automaticPriceDropMinPriceField = useField(ListingFlowFormField.AutomaticPriceDropMinPrice);
  const originalPriceValue = originalPrice && convertMoneyToNumber(originalPrice);

  const initialSellingPrice = automaticPriceDropAnchorPrice || price;

  const formattedInitialSellingPrice = initialSellingPrice
    ? formatMoney(intl, initialSellingPrice)
    : undefined;

  const maxPriceRequired = validators.moneySubUnitAmountAtMost(
    'Price cannot be higher than listing price.',
    initialSellingPrice?.amount
  );

  const calculatedMinimumPrice = calculateMinimumPrice(
    originalPriceValue,
    pricingRestrictions,
    tags,
    itemType
  );
  const minPrice = new Money(calculatedMinimumPrice, currencyConfig.currency);

  const minPriceRequired = validators.moneySubUnitAmountAtLeast(
    intl.formatMessage(
      { id: 'EditListingPricingForm.priceTooLow' },
      { minPrice: formatMoney(intl, minPrice), brandName: shopName }
    ),
    calculatedMinimumPrice
  );

  const priceRequired = validators.required('Add a valid minimum price.');

  const priceValidators = automaticPriceDropEnabled
    ? validators.composeValidators(priceRequired, minPriceRequired, maxPriceRequired)
    : undefined;

  const border = `1px solid ${
    automaticPriceDropEnabled ? defaultTreetStyles.green60 : defaultTreetStyles.gray20
  }`;

  const clearFormPriceDropValues = () => {
    form.change(ListingFlowFormField.AutomaticPriceDropMinPrice, undefined);
    form.change(ListingFlowFormField.AutomaticPriceDropAnchorPrice, undefined);
  };

  const setFormPriceDropValues = (priceValue: MoneyType | undefined) => {
    if (!priceValue) return;
    form.change(
      ListingFlowFormField.AutomaticPriceDropMinPrice,
      getRecommendedMinPriceForPriceDrop(priceValue)
    );
    form.change(ListingFlowFormField.AutomaticPriceDropAnchorPrice, priceValue);
  };
  return (
    <Box
      padding={3}
      my={2}
      maxWidth="540px"
      style={{
        border: `1px solid ${defaultTreetStyles.gray20}`,
        backgroundColor: defaultTreetStyles.gray10,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          style={{ columnGap: '8px', rowGap: '8px' }}
          flexWrap="wrap"
        >
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} id="price-drop-label">
            {automaticPriceDropEnabled ? 'Price Drop Is On' : 'Enable Price Drop'}
          </TypographyWrapper>
          <Box
            borderRadius="32px"
            padding="4px 8px"
            display="flex"
            alignItems="center"
            style={{ border }}
          >
            {automaticPriceDropEnabled && (
              <Box paddingRight="3px" display="flex" alignItems="center">
                <IconCheckCircleFilled height="17" width="17" color={defaultTreetStyles.green60} />
              </Box>
            )}
            <TypographyWrapper
              variant="body2"
              weight={TypographyWeight.Bold}
              typographyOverrides={{
                style: {
                  textTransform: 'uppercase',
                  color: `${
                    automaticPriceDropEnabled
                      ? defaultTreetStyles.green60
                      : defaultTreetStyles.gray20
                  }`,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              Recommended
            </TypographyWrapper>
          </Box>
        </Box>
        <FieldSwitch
          name={ListingFlowFormField.AutomaticPriceDropEnabled}
          data={{ label: '', value: automaticPriceDropEnabled }}
          color="default"
          inputProps={{
            'aria-labelledby': 'price-drop-label',
          }}
          formControlLabelProps={{
            style: {
              margin: 'unset',
            },
          }}
        />
      </Box>
      <Box>
        <TypographyWrapper component="div" variant="body1">
          {automaticPriceDropEnabled ? (
            <>
              Automatically reduce the price of your item by {automaticPriceDropPercentage}% of your
              initial selling price{' '}
              {formattedInitialSellingPrice ? ` (${formattedInitialSellingPrice})` : ''} each week
              until it hits your set minimum price.
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                marginY={2}
                style={{ columnGap: '8px' }}
              >
                <TypographyWrapper
                  variant="body2"
                  weight={TypographyWeight.Bold}
                  typographyOverrides={{
                    style: {
                      textTransform: 'uppercase',
                      color: defaultTreetStyles.gray80,
                      flex: '0 0 auto',
                    },
                  }}
                >
                  Minimum Price
                </TypographyWrapper>
                <FieldCurrencyInput
                  id={ListingFlowFormField.AutomaticPriceDropMinPrice}
                  name={ListingFlowFormField.AutomaticPriceDropMinPrice}
                  autocomplete="off"
                  placeholder="0"
                  validate={priceValidators}
                  inputValue={automaticPriceDropMinPriceField.input.value}
                  input={automaticPriceDropMinPriceField.input}
                  meta={automaticPriceDropMinPriceField.meta}
                  boxContainerProps={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    style: {
                      columnGap: '8px',
                    },
                  }}
                  inputTypographyOverrides={{
                    style: {
                      maxWidth: '100px',
                    },
                  }}
                />
              </Box>
              <OnChange name={ListingFlowFormField.Price}>
                {(value) => {
                  if (value && form.getFieldState(ListingFlowFormField.Price)?.modified) {
                    // If the listing is published or already has a saved price drop config,
                    // don't update the form values. The selling price will have changed via
                    // price drop but the anchor price should be preserved here.
                    if (
                      currentListing.attributes?.state !== LISTING_STATE_PUBLISHED ||
                      savedListing.attributes?.publicData?.priceDropConfig?.isActive
                    ) {
                      setFormPriceDropValues(value);
                    }
                  }
                }}
              </OnChange>
            </>
          ) : (
            <>
              Our suggested selling price is based on price drop being enabled. If you do decide not
              to automatically drop the price of your item, consider lowering your selling price.
            </>
          )}
        </TypographyWrapper>
      </Box>
      <OnChange name={ListingFlowFormField.AutomaticPriceDropEnabled}>
        {(value) => {
          if (value === false) {
            // Reset Min Price & Percentage values if price drop is disabled.
            clearFormPriceDropValues();
          } else if (form.getFieldState(ListingFlowFormField.AutomaticPriceDropEnabled)?.modified) {
            // Reset the price drop properties in the form using the latest price value.
            const priceValue = form.getFieldState(ListingFlowFormField.Price)?.value;
            setFormPriceDropValues(priceValue);
          }
        }}
      </OnChange>
    </Box>
  );
};

const Price: FC = () => {
  const { priceHelperLabel } = useListingFlowConfig();
  const {
    listingFlowConfig: { shouldHideMsrpOnNewListings },
  } = useShopConfig();

  return (
    <>
      <Heading>Price</Heading>
      {!!priceHelperLabel && <Subheading>{priceHelperLabel}</Subheading>}
      {!shouldHideMsrpOnNewListings && <OriginalPrice />}
      <SellingPrice />
      <SellerPriceInfo />
      <AutomaticPriceDrop />
    </>
  );
};

export default Price;
